
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Checkbox } from '@/components/ui/checkbox';
import { WorkoutType } from '@/models/WorkoutType';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface WorkoutTypeFiltersProps {
  workoutTypes: WorkoutType[];
  selectedWorkoutTypes: Set<string>;
  onWorkoutTypeFilter: (workoutTypeId: string) => void;
}

const WorkoutTypeFilters = ({
  workoutTypes,
  selectedWorkoutTypes,
  onWorkoutTypeFilter,
}: WorkoutTypeFiltersProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [showModal, setShowModal] = useState(false);
  const [tempSelection, setTempSelection] = useState<Set<string>>(new Set());

  // When 'all' is selected, show "All" as active
  const isAllSelected = selectedWorkoutTypes.has('all');
  
  // Show first 4 workout types on mobile (2 rows of 2)
  const visibleWorkoutTypes = workoutTypes.slice(0, 4);
  // Always show "Show More" button when there are 5 or more workout types
  const hasMoreFilters = workoutTypes.length >= 5;

  const handleShowMore = () => {
    setTempSelection(new Set(selectedWorkoutTypes));
    setShowModal(true);
  };

  const handleToggleWorkoutType = (workoutTypeId: string) => {
    const newSelection = new Set(tempSelection);
    
    if (workoutTypeId === 'all') {
      newSelection.clear();
      newSelection.add('all');
    } else if (newSelection.has(workoutTypeId)) {
      newSelection.delete(workoutTypeId);
      if (newSelection.size === 0 || (newSelection.size === 1 && newSelection.has('all'))) {
        newSelection.clear();
        newSelection.add('all');
      }
    } else {
      newSelection.delete('all');
      newSelection.add(workoutTypeId);
    }
    
    setTempSelection(newSelection);
  };

  const handleApply = () => {
    // Apply each change through the parent handler
    const currentIds = Array.from(selectedWorkoutTypes);
    const tempIds = Array.from(tempSelection);
    
    // Clear current selection by toggling off all that aren't in temp
    currentIds.forEach(id => {
      if (!tempSelection.has(id)) {
        onWorkoutTypeFilter(id);
      }
    });
    
    // Add new selections
    tempIds.forEach(id => {
      if (!selectedWorkoutTypes.has(id)) {
        onWorkoutTypeFilter(id);
      }
    });
    
    setShowModal(false);
  };

  const handleClearAll = () => {
    setTempSelection(new Set(['all']));
  };

  const filterContent = (
    <>
      {/* All option */}
      <div className="flex items-center space-x-3">
        <Checkbox
          id="all"
          checked={tempSelection.has('all')}
          onCheckedChange={() => handleToggleWorkoutType('all')}
        />
        <label
          htmlFor="all"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
        >
          {t('common.all')}
        </label>
      </div>

      {/* All workout types */}
      {workoutTypes.map((workoutType) => (
        <div key={workoutType.id} className="flex items-center space-x-3">
          <Checkbox
            id={workoutType.id}
            checked={tempSelection.has(workoutType.id)}
            onCheckedChange={() => handleToggleWorkoutType(workoutType.id)}
          />
          <label
            htmlFor={workoutType.id}
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex items-center gap-2"
          >
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: workoutType.color }}
            />
            {workoutType.name}
          </label>
        </div>
      ))}
    </>
  );

  return (
    <>
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-muted-foreground">
          {t('calendar.workoutTypeFilters')}
        </h3>
        <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2">
          <Button
            variant={isAllSelected ? 'default' : 'outline'}
            size="sm"
            onClick={() => onWorkoutTypeFilter('all')}
            className="w-full md:w-auto"
          >
            {t('common.all')}
          </Button>
          {visibleWorkoutTypes.map((workoutType) => (
            <Button
              key={workoutType.id}
              variant={selectedWorkoutTypes.has(workoutType.id) ? 'default' : 'outline'}
              size="sm"
              onClick={() => onWorkoutTypeFilter(workoutType.id)}
              className="w-full md:w-auto"
              style={{
                backgroundColor: selectedWorkoutTypes.has(workoutType.id) 
                  ? workoutType.color 
                  : undefined,
                borderColor: workoutType.color,
                color: selectedWorkoutTypes.has(workoutType.id) 
                  ? '#ffffff' 
                  : workoutType.color,
              }}
            >
              {workoutType.name}
            </Button>
          ))}
          {hasMoreFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleShowMore}
              className="flex items-center gap-2 w-full md:w-auto"
            >
              <Plus className="h-4 w-4" />
              {t('common.showMore')}
            </Button>
          )}
        </div>
      </div>

      {isMobile ? (
        <Drawer open={showModal} onOpenChange={setShowModal}>
          <DrawerContent className="h-[100dvh] flex flex-col">
            <DrawerHeader className="shrink-0 text-left">
              <DrawerTitle>{t('calendar.selectWorkoutTypes')}</DrawerTitle>
              <DrawerDescription>
                {t('calendar.selectWorkoutTypesDescription')}
              </DrawerDescription>
            </DrawerHeader>
            
            <div className="flex-1 overflow-y-auto px-4">
              <div className="space-y-4 py-2">
                {filterContent}
              </div>
              
              {/* Footer buttons inside scrollable area */}
              <div className="sticky bottom-0 bg-background pt-4 pb-4 safe-bottom flex flex-col gap-2 border-t mt-4">
                <Button onClick={handleApply} className="w-full">
                  {t('common.apply')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClearAll}
                  className="w-full"
                >
                  {t('common.clearAll')}
                </Button>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('calendar.selectWorkoutTypes')}</DialogTitle>
              <DialogDescription>
                {t('calendar.selectWorkoutTypesDescription')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="max-h-[60vh] overflow-y-auto py-2">
              <div className="space-y-4">
                {filterContent}
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={handleClearAll}
              >
                {t('common.clearAll')}
              </Button>
              <Button onClick={handleApply}>
                {t('common.apply')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default WorkoutTypeFilters;
